// ==== Servicio ====
import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { map, catchError, skip, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { IMenuNodeResponse, IMenuUIItem, Nullable } from './menu.dto';
import { ConnectionServices } from '../authentication/connection/connection.service';
import { GetMenuCarrierCommand, IGetMenuCarrierCommandParameters } from './command/get-menu.command';
import { MenuCode } from './menuCode.enum';
import { Database, objectVal, ref } from '@angular/fire/database';

@Injectable({ providedIn: 'root' })
export class MenuService {
  // opcional: cachear crudo para rearmar labels al cambiar idioma
  private _rawCache: IMenuNodeResponse[] | null = null;
  private readonly excludedCodes = ['NOT', 'MSG', 'CLI_E']; //Excluidos para no mostrarlos en el sidebar 
   // ----- NUEVO: watcher RTDB -----
  private rtdbMenuSub?: Subscription;
  private menuUpdatedSubject = new Subject<void>();
  /** Emite cada vez que el flag de RTDB cambia y se refresca el menú */
  public readonly menuUpdated$: Observable<void> = this.menuUpdatedSubject.asObservable();

  constructor(
    private _connectionService: ConnectionServices,
    private translate: TranslateService,
    private db: Database,   
  ) {}

  /** Devuelve el menú listo para la UI (con label/link/icon mapeados) */
  public getCarrierMenu(): Observable<IMenuUIItem[]> {
    const command = new GetMenuCarrierCommand();
    command.setParameters(<IGetMenuCarrierCommandParameters>{ portal: '1' });

    return this._connectionService.Request(command).pipe(
      map((data: IMenuNodeResponse[]) => {
        this._rawCache = data ?? [];
        return this.buildCarrierMenu(this._rawCache);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  /** Re-armar solo las etiquetas (por cambio de idioma) sin pegarle al backend */
  public rebuildMenuForCurrentLang(): IMenuUIItem[] {
    return this.buildCarrierMenu(this._rawCache ?? []);
  }

  // ===== Helpers de mapeo =====

  private buildCarrierMenu(raw: IMenuNodeResponse[]): IMenuUIItem[] {
    return (raw ?? [])
      .map(n => this.mapAndFilterNode(n))
      .filter((n): n is IMenuUIItem => n !== null);
  }

  /**
   * Mapea un nodo aplicando filtro recursivo.
   * Devuelve null si el nodo (o todos sus hijos) quedan excluidos.
  */
  private mapAndFilterNode(n: IMenuNodeResponse): IMenuUIItem | null {
    // 1) Si el propio nodo está excluido, no lo mostramos
    if (this.excludedCodes.includes(n.code)) {
      return null;
    }

    // 2) Procesar hijos recursivamente y descartar los null
    const mappedChildren = (n.subItems ?? [])
      .map(child => this.mapAndFilterNode(child))
      .filter((c): c is IMenuUIItem => c !== null);

    // 3) Si tiene hijos válidos, el link debe ser '' (menú desplegable).
    //    Si no tiene hijos válidos, usar el link del propio nodo.
    const hasChildren = mappedChildren.length > 0;

    return {
      label: this.pickLabel(n),
      link: hasChildren ? '' : this.pickLink(n),
      icon: this.parseIcon(n.iconUrl),
      subItems: hasChildren ? mappedChildren : undefined,
      code: n.code,
    };
  }

  private mapNode(n: IMenuNodeResponse): IMenuUIItem {
    const children = (n.subItems ?? []).map((c) => this.mapNode(c));

    return {
      label: this.pickLabel(n),
      link: children.length ? '' : this.pickLink(n),
      icon: this.parseIcon(n.iconUrl),          // ← toma iconUrl como está
      subItems: children.length ? children : undefined,
      code: n.code
    };
  }

  private pickLabel(n: IMenuNodeResponse): string {
    const lang = (this.translate.currentLang || this.translate.getDefaultLang() || 'es-ar')
                   .toLowerCase();
    return lang.startsWith('es-ar') ? n.nameEs : n.nameEn;
  }

  private pickLink(n: IMenuNodeResponse): string {
    const chosen = n.angOldUrl;//n.angNewUrl;
    return this.sanitizeUrl(chosen);
  }

  private pickLinkComponent(n: IMenuNodeResponse): string {
    // Si está migrado usa angNewUrl, si no angOldUrl; quita '?' final si quedó suelta
    const chosen = n.angOldUrl;
    return this.sanitizeUrl(chosen);
  }

  private sanitizeUrl(url: Nullable<string>): string {
    if (!url) return '';
    return url.trim().replace(/\?$/, '');
  }

  private parseIcon(iconUrl: Nullable<string>): string | undefined {
    const v = iconUrl?.trim();
    return v && v.length ? v : undefined;
  }


  /** Devuelve el link (new vs old) para un code. Si aún no cargaste, lo carga. */
  public linkForCode$(code: MenuCode): Observable<string> {
    if (this._rawCache) return of(this.computeLinkForCode(code) ?? '');
    return this.getCarrierMenu().pipe(map(() => this.computeLinkForCode(code) ?? ''));
  }

  /** Devuelve angMigrated para un code; null si no existe. */
  public migratedForCode$(code: MenuCode): Observable<boolean | null> {
    if (this._rawCache) return of(this.computeMigratedForCode(code));
    return this.getCarrierMenu().pipe(map(() => this.computeMigratedForCode(code)));
  }

  /** Si ya cargaste antes el menú, podés usar esta sync. */
  public linkForCodeSync(code: MenuCode): string {
    return this.computeLinkForCode(code) ?? '';
  }

  // ===== privados =====
  private computeLinkForCode(code: MenuCode): string | null {
    const node = this.findNodeByCode(code as unknown as string, this._rawCache ?? []);
    return node ? this.pickLinkComponent(node) : null;
  }

  private computeMigratedForCode(code: MenuCode): boolean | null {
    const node = this.findNodeByCode(code as unknown as string, this._rawCache ?? []);
    return node ? !!node.angMigrated : null;
  }
  
  private findNodeByCode(code: string, nodes: IMenuNodeResponse[]): IMenuNodeResponse | null {
    for (const n of nodes) {
      if (n.code === code) return n;
      const child = this.findNodeByCode(code, n.subItems ?? []);
      if (child) return child;
    }
    return null;
  }


  // ====== 👇 NUEVO: suscribirse a RTDB y refrescar menú ======
  /**
   * Comienza a escuchar el nodo de RTDB y cuando cambia, refresca el menú desde el backend.
   * @param path Ruta en RTDB (por defecto 'menu_updated')
   */
  public startMenuRTDBWatcher(path: string = 'Admin_params/menu_carrier_updated'): void {
    // Evitar duplicar suscripción si ya está corriendo
    if (this.rtdbMenuSub) return;
    console.log('escuchando cambio rtdb!');
    // Si querés usar userId:
    // const uid = String(this._identityService.userId);
    // const path = `flags/dashboards/operations_control/carrier/${uid}/menu_updated`;

    this.rtdbMenuSub = objectVal<boolean | number | null>(ref(this.db, path))
      .pipe(
        // No reaccionar al valor inicial
        skip(1),
        // Anti-rebote
        debounceTime(250),
        // Evitar repetidos
        distinctUntilChanged(),
        // Cuando cambia el flag, recargo el menú (esto también actualiza el _rawCache)
        switchMap(() => this.getCarrierMenu()),
        // Nunca romper el stream por errores
        catchError(err => {
          console.error('[MenuService] RTDB watcher error:', err);
          return of([] as IMenuUIItem[]);
        })
      )
      .subscribe(() => {
        // Aviso a quienes se suscriban que hay menú actualizado (cache ya listo)
        this.menuUpdatedSubject.next();
      });
  }

  /** Detiene la suscripción a RTDB. */
  public stopMenuRTDBWatcher(): void {
    if (this.rtdbMenuSub) {
      this.rtdbMenuSub.unsubscribe();
      this.rtdbMenuSub = undefined;
    }
  }

  //DropDown TopBar 
  /** Devuelve el item de UI (label/link/icon) para un code, o null si no existe */
  public detailForCode$(code: MenuCode) {
    const build = (): IMenuUIItem | null => {
      const node = this.findNodeByCode(code as unknown as string, this._rawCache ?? []);
      return node ? this.mapNode(node) : null;
    };

    if (this._rawCache) return of(build());
    // si aún no cargó, carga primero y luego arma el item
    return this.getCarrierMenu().pipe(map(() => build()));
  }

  /** Devuelve varios items por code, en el orden dado (filtra nulls) */
  public detailsForCodes$(codes: MenuCode[]) {
    return combineLatest(codes.map(c => this.detailForCode$(c))).pipe(
      map(arr => arr.filter((x): x is IMenuUIItem => !!x))
    );
  }
}
